// The Google dashboard's two SECONDARY TABLES as arrangeable widgets, plus each
// one's own column vocabulary. PURE constants + sanitizers, a LEAF module.
//
// ⚠️ LEAF ON PURPOSE, for exactly the reason googleAdsChartCatalog.ts documents:
// googleAdsViewPrefs imports these constants to build its module-level default
// state, so anything here that imported a COMPONENT could leave a constant
// `undefined` at init in the production bundle and white-screen the page. The
// only import is the metric catalogue, itself a leaf (it imports types alone).
//
// ── WHY TWO DIFFERENT COLUMN VOCABULARIES, NOT ONE ─────────────────────────
// The two selectors are different by NATURE and are deliberately not unified:
//
//  · ChannelBreakdown rows carry a FULL `.metrics` block, so its selector is the
//    shared metric catalogue — minus the impression-share family (see below).
//  · SearchTerms rows carry only a handful of SCALAR fields, so its selector can
//    only ever offer those. Adding a metric the endpoint does not return would
//    need a query change; a column that renders a fabricated blank is worse than
//    no column. If specific extra search-term metrics are wanted later, that is a
//    data-layer task, not a widening of this list.

import {
  GOOGLE_ADS_METRICS,
  type GoogleAdsMetricDef,
} from '../../lib/googleAdsMetrics';
import type { GoogleAdsMetricKey } from '../../types/googleAdsReporting';

// ---------------------------------------------------------------------------
// Which table widgets are on screen
// ---------------------------------------------------------------------------

export type GoogleTableKey = 'channelBreakdown' | 'searchTerms';

export const GOOGLE_TABLES: Array<{ key: GoogleTableKey; label: string; hint: string }> = [
  {
    key: 'channelBreakdown',
    label: 'Performance by campaign type',
    hint: 'PMAX vs Search vs Display — the split a blended CPA hides',
  },
  {
    key: 'searchTerms',
    label: 'Search terms',
    hint: 'What people actually typed to trigger the ads',
  },
];

export const DEFAULT_GOOGLE_TABLES: GoogleTableKey[] = ['channelBreakdown', 'searchTerms'];

const TABLE_KEYS = new Set<GoogleTableKey>(GOOGLE_TABLES.map((t) => t.key));

/**
 * Drop unknowns, keep catalogue order. Empty is ALLOWED — hiding both tables is a
 * legitimate choice, exactly as hiding every chart is.
 */
export function sanitizeTables(stored: readonly string[]): GoogleTableKey[] {
  const wanted = new Set(stored.filter((k): k is GoogleTableKey => TABLE_KEYS.has(k as GoogleTableKey)));
  return GOOGLE_TABLES.filter((t) => wanted.has(t.key)).map((t) => t.key);
}

// ---------------------------------------------------------------------------
// ChannelBreakdown columns — the metric catalogue MINUS impression share
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ THE IMPRESSION-SHARE FAMILY IS EXCLUDED FROM THIS TABLE, AND THE SELECTOR
 * MUST NOT BE ABLE TO PUT IT BACK.
 *
 * Only Search-network delivery reports impression share, so a PMAX or Display row
 * would show "—" in every share column — and a reader scanning a column of dashes
 * concludes those campaign types are losing the auction, when in truth they are
 * simply unmeasured there. That is a wrong conclusion about real money, which is
 * why the exclusion has always been documented at the top of ChannelBreakdown.tsx.
 * Offering the share metrics in a picker would reintroduce exactly that misread,
 * one click away. The competitiveness block owns the auction story.
 *
 * Listed EXPLICITLY rather than derived from `nullable`, so the intent is legible
 * — and a test pins the two to each other in BOTH directions, so a future
 * nullable metric can't slip in and an IS metric can't drop out of the list.
 */
export const CHANNEL_BREAKDOWN_EXCLUDED_METRICS: GoogleAdsMetricKey[] = [
  'searchImpressionShare',
  'searchTopImpressionShare',
  'searchAbsoluteTopImpressionShare',
  'searchBudgetLostImpressionShare',
  'searchRankLostImpressionShare',
  'searchClickShare',
  'contentImpressionShare',
];

const EXCLUDED = new Set<GoogleAdsMetricKey>(CHANNEL_BREAKDOWN_EXCLUDED_METRICS);

/** Every metric this table may show, in catalogue order. */
export const CHANNEL_BREAKDOWN_METRICS: GoogleAdsMetricDef[] = GOOGLE_ADS_METRICS.filter(
  (m) => !EXCLUDED.has(m.key),
);

export const CHANNEL_BREAKDOWN_METRIC_ORDER: GoogleAdsMetricKey[] =
  CHANNEL_BREAKDOWN_METRICS.map((m) => m.key);

/** The seven columns the table has always shown. */
export const CHANNEL_BREAKDOWN_DEFAULT_COLUMNS: GoogleAdsMetricKey[] = [
  'cost',
  'impressions',
  'clicks',
  'ctr',
  'conversions',
  'costPerConversion',
  'roas',
];

/**
 * Drop unknowns AND anything excluded, dedupe, restore catalogue order; an empty
 * selection falls back to the default set.
 *
 * ⚠️ Empty falls BACK here, unlike the tree's attribute columns where empty is the
 * intended default: a table of nothing but campaign-type names shows no
 * performance at all, which reads as broken rather than as a choice.
 */
export function sanitizeChannelColumns(stored: readonly string[]): GoogleAdsMetricKey[] {
  const known = new Set(CHANNEL_BREAKDOWN_METRIC_ORDER);
  const picked = new Set<GoogleAdsMetricKey>();
  for (const k of stored) {
    if (known.has(k as GoogleAdsMetricKey)) picked.add(k as GoogleAdsMetricKey);
  }
  const ordered = CHANNEL_BREAKDOWN_METRIC_ORDER.filter((k) => picked.has(k));
  return ordered.length > 0 ? ordered : [...CHANNEL_BREAKDOWN_DEFAULT_COLUMNS];
}

// ---------------------------------------------------------------------------
// SearchTerms columns — its OWN fields, nothing else
// ---------------------------------------------------------------------------

export type SearchTermColumnKey =
  | 'matchType'
  | 'cost'
  | 'impressions'
  | 'clicks'
  | 'ctr'
  | 'conversions'
  | 'costPerConversion';

export const SEARCH_TERM_COLUMNS: Array<{ key: SearchTermColumnKey; label: string; hint?: string }> =
  [
    { key: 'matchType', label: 'Match type', hint: 'Broad, phrase or exact' },
    { key: 'cost', label: 'Cost' },
    { key: 'impressions', label: 'Impr.' },
    { key: 'clicks', label: 'Clicks' },
    { key: 'ctr', label: 'CTR' },
    { key: 'conversions', label: 'Conv.' },
    { key: 'costPerConversion', label: 'Cost / conv.' },
  ];

/**
 * ⚠️ The search TERM itself is not in that list and is not toggleable — it is the
 * row's identity, and a table of anonymous numbers would be unreadable.
 */
export const SEARCH_TERM_IDENTITY_COLUMN = 'searchTerm' as const;

export const SEARCH_TERM_DEFAULT_COLUMNS: SearchTermColumnKey[] = SEARCH_TERM_COLUMNS.map(
  (c) => c.key,
);

const SEARCH_TERM_KEYS = new Set<SearchTermColumnKey>(SEARCH_TERM_COLUMNS.map((c) => c.key));

/**
 * Drop unknowns (including the identity column, which is never stored because it
 * cannot be turned off), dedupe, restore catalogue order; empty → the defaults.
 */
export function sanitizeSearchTermColumns(stored: readonly string[]): SearchTermColumnKey[] {
  const picked = new Set<SearchTermColumnKey>();
  for (const k of stored) {
    if (SEARCH_TERM_KEYS.has(k as SearchTermColumnKey)) picked.add(k as SearchTermColumnKey);
  }
  const ordered = SEARCH_TERM_COLUMNS.filter((c) => picked.has(c.key)).map((c) => c.key);
  return ordered.length > 0 ? ordered : [...SEARCH_TERM_DEFAULT_COLUMNS];
}
